import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { StickyQuoteCTA } from '@/components/custom/StickyQuoteCTA';
import UrgentBanner from '@/components/custom/UrgentBanner';

const navigation = [
  { name: 'Accueil', href: '/' },
  { 
    name: 'Services', 
    children: [
      { name: 'Rénovation complète', href: '/renovation-complete' },
      { name: 'Rénovation partielle', href: '/renovation-partielle' },
      { name: 'Carrelage & revêtements', href: '/carrelage-revetements' },
      { name: 'Sanitaires', href: '/sanitaires' },
      { name: 'Plans 3D', href: '/plans-3d' },
    ]
  },
  { name: 'Galerie', href: '/galerie' },
  { name: 'Simulateur ROI', href: '/simulateur', highlight: true },
  { name: 'À propos', href: '/a-propos' },
];

export const MainLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Bannière urgente fixe */}
      <UrgentBanner />
      
      {/* Top bar */}
      <div className="bg-[#2B2B2B] text-white py-2 px-3 sm:px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-[10px] xs:text-xs sm:text-sm">
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="truncate">🇨🇭 Suisse romande : GE, VD, VS, FR, NE</span>
          </div>
          <div className="hidden sm:block flex-shrink-0">
            <span className="font-medium whitespace-nowrap">Devis sous 48h</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm" data-testid="site-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <h1 className="text-base sm:text-xl lg:text-2xl font-bold leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <span className="text-[#2B2B2B]">Rénovation</span>{' '}
                <span className="text-[#336DBF]">Salle de Bains</span>
              </h1>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {navigation.map((item) => (
                item.children ? (
                  <div key={item.name} className="relative group">
                    <button className="text-slate-700 hover:text-[#336DBF] font-medium transition-colors">
                      {item.name}
                    </button>
                    <div className="absolute top-full left-0 mt-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="bg-white rounded-lg shadow-lg border border-slate-200 py-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#336DBF] transition-colors"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`font-medium transition-colors ${
                      item.highlight 
                        ? 'text-[#336DBF] hover:text-[#2F62AA] font-bold'
                        : location.pathname === item.href 
                          ? 'text-[#336DBF]' 
                          : 'text-slate-700 hover:text-[#336DBF]'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Button 
                onClick={() => navigate('/devis')}
                className="bg-[#336DBF] hover:bg-[#2F62AA] text-white font-medium px-6"
                data-testid="header-quote-cta"
              >
                Demander un devis
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-80">
                <nav className="flex flex-col space-y-4 mt-8">
                  {navigation.map((item) => (
                    <div key={item.name}>
                      {item.children ? (
                        <div className="space-y-2">
                          <div className="font-semibold text-[#2B2B2B] text-lg">{item.name}</div>
                          <div className="pl-4 space-y-2">
                            {item.children.map((child) => (
                              <Link
                                key={child.href}
                                to={child.href}
                                onClick={() => setMobileMenuOpen(false)}
                                className="block text-slate-600 hover:text-[#336DBF] py-1"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={item.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block text-slate-700 hover:text-[#336DBF] font-medium text-lg"
                        >
                          {item.name}
                        </Link>
                      )}
                    </div>
                  ))}
                  <Button 
                    onClick={() => {
                      navigate('/devis');
                      setMobileMenuOpen(false);
                    }}
                    className="bg-[#336DBF] hover:bg-[#2F62AA] text-white font-medium w-full py-6"
                  >
                    Demander un devis
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Sticky CTA for Mobile */}
      <StickyQuoteCTA />

      {/* Footer */}
      <footer className="bg-[#2B2B2B] text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Rénovation Salle de Bains
              </h2>
              <p className="text-slate-300 mb-4">
                Service premium de rénovation de salle de bains en Suisse romande.
                Solution clé en main, finitions impeccables.
              </p>
              <p className="text-sm text-slate-400">
                Une marque de <span className="font-semibold">Homely Solutions</span>
              </p>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-semibold mb-4">Services</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/renovation-complete" className="hover:text-[#336DBF] transition-colors">Rénovation complète</Link></li>
                <li><Link to="/renovation-partielle" className="hover:text-[#336DBF] transition-colors">Rénovation partielle</Link></li>
                <li><Link to="/carrelage-revetements" className="hover:text-[#336DBF] transition-colors">Carrelage & revêtements</Link></li>
                <li><Link to="/sanitaires" className="hover:text-[#336DBF] transition-colors">Sanitaires</Link></li>
                <li><Link to="/plans-3d" className="hover:text-[#336DBF] transition-colors">Plans 3D</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="font-semibold mb-4">Informations</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/a-propos" className="hover:text-[#336DBF] transition-colors">À propos</Link></li>
                <li><Link to="/galerie" className="hover:text-[#336DBF] transition-colors">Galerie</Link></li>
                <li><Link to="/cgv" className="hover:text-[#336DBF] transition-colors">CGV</Link></li>
                <li><Link to="/mentions-legales" className="hover:text-[#336DBF] transition-colors">Mentions légales</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>© {new Date().getFullYear()} Rénovation de Salle de Bains - Homely Solutions. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};